import { useEffect, useState } from 'react';

// Reactive viewport check used by pages to swap inline-styled layouts between
// desktop and mobile (<=768px). 768px matches Bootstrap's `md` breakpoint, so
// the JS-driven layout flips align with any `col-md-*` classes already in use.
export const useIsMobile = (maxWidthPx = 768) => {
  const query = `(max-width: ${maxWidthPx}px)`;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [query]);
  return isMobile;
};

// Household gemstone tiers — shared between the new-client wizard (picker)
// and the client list/detail pages (display). 'diamond' types (diamond /
// blue diamond / red diamond) require a second advisor on the household.
export const GEMSTONE_OPTIONS = [
  { key: 'opal', label: 'Opal', color: '#c9b6e4', diamond: false },
  { key: 'ruby', label: 'Ruby', color: '#7d0d1b', diamond: false },
  { key: 'diamond', label: 'Diamond', color: '#a9d6e5', diamond: true },
  { key: 'blue_diamond', label: 'Blue Diamond', color: '#1d4ed8', diamond: true },
  { key: 'red_diamond', label: 'Red Diamond', color: '#e63946', diamond: true },
];
export const DIAMOND_GEMSTONES = new Set(GEMSTONE_OPTIONS.filter(g => g.diamond).map(g => g.key));
export const gemstoneInfo = (key) => GEMSTONE_OPTIONS.find(g => g.key === key) || null;

// Client workflow stages — the four "D" steps of the PCA process. Shared by
// the new-client wizard (picker), the client list (pill) and the client
// detail page (inline picker + edit modal) so the labels can't drift apart.
// Keys match VALID_WORKFLOW_STATUSES on the backend, and select which step
// is lit on the client portal dashboard's progress bar. (The portal keeps
// its own copy of this list on purpose - portal/ imports nothing from
// routes/ so it can be extracted into its own app.)
export const WORKFLOW_STAGE_OPTIONS = [
  { key: 'discovery', label: 'Discovery', hint: 'Collecting documents',     icon: 'search',        color: '#d09b2c' },
  { key: 'design',    label: 'Design',    hint: 'Building the bucket plan', icon: 'pencil-square', color: '#0069a6' },
  { key: 'delivery',  label: 'Delivery',  hint: 'Moving accounts',          icon: 'box-seam',      color: '#0a2947' },
  { key: 'dedicate',  label: 'Dedicate',  hint: 'Ongoing care',             icon: 'heart',         color: '#1a7f4e' },
];
// What a client gets when nobody picks a stage - mirrors the column's
// server_default so the wizard's initial selection matches the database.
export const DEFAULT_WORKFLOW_STAGE = 'discovery';
export const workflowStageInfo = (key) => WORKFLOW_STAGE_OPTIONS.find(s => s.key === key) || null;

// Fetches a one-time document download/view URL exactly once and hands the
// browser an in-memory blob: URL instead of ever navigating a tab to the
// (single-use) server URL directly. That matters because a plain navigation
// leaves the link sitting where something other than the user's own click
// can independently fetch it first and burn its one use — e.g. a browser's
// built-in PDF viewer probing the URL, or a corporate AV/proxy that scans
// links before letting the navigation through — which is exactly what caused
// "already been used" errors on a normal first click. A blob: URL is
// in-memory only and isn't independently fetchable by anything.
export const fetchAsBlobUrl = async (url) => {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Could not retrieve the file (HTTP ${resp.status}).`);
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
};

// format large numbers for activity metric card counts
export const formatCount = (num) => {
  if (num === null || num === undefined || !Number.isFinite(num)) return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(3).replace(/\.?0+$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return num.toString();
};

// build N-30 date range for activity charts 
export const buildDateRange = (dailyCounts, days = 30, defaultValues = { count: 0 }) => {
  const allDates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    allDates.push(date.toISOString().split('T')[0]);
  }

  const dataMap = {};
  dailyCounts.forEach(d => {
    dataMap[d.date] = d;
  });

  return allDates.map(date => ({
    date,
    ...defaultValues,
    ...(dataMap[date] || {})
  }));
};

export function getIdenfyValueFromPerl(perlInt){
  switch (parseInt(perlInt)) {
    case 2:
      return 'pending';
    case 1:
      return 'complete';
    case 3:
      return 'denied';
    case 4:
      return 'blocked';
    case 5: 
      return 'aml-blocked';
    case 6:
      return 'internal-review';
    case 0:
      return 'not-initiated';
    case 7:
      return 'expired';
    case 8:
      return 'waived';
    default:
      return 'unknown';
  }
}

export function getPerlValueFromStatus(thisStatus){
  switch (thisStatus) {
    case 'pending':
      return 2;
    case 'complete':
      return 1;
    case 'denied':
      return 3;
    case 'blocked':
      return 4;
    case 'aml-blocked':
      return 5; 
    case 'internal-review':
      return 6;
    case 'not-initiated':
      return 0;
    case 'expired':
      return 7;
    case 'waived':
      return 8;
    default:
      return -1;
  }
}

export function getStudioStatusFromPerl(perlInt) {
  switch (parseInt(perlInt)) {
    case 0:
      return 'Pending';
    case 1:
      return 'Approved';
    case 2: 
      return 'Denied';
    case 3: 
      return 'Suspended';
    case 4: 
      return 'Suspended twice';
    case 5: 
      return 'Cancelled';
    case 6: 
      return 'Closed';
    default:
      return 'Unknown';
  }
}

/* KYC Statuses */
export const idenfyStatuses = [
  {desc: 'pending', int_value: 2}, 
  {desc: 'complete', int_value: 1},
  {desc: 'denied', int_value: 3},
  {desc: 'blocked', int_value: 4},
  {desc: 'aml-blocked', int_value: 5},
  {desc: 'internal-review', int_value: 6},
  {desc: 'not-initiated', int_value: 0},
  {desc: 'expired', int_value: 7},
  {desc: 'waived', int_value: 8},
  {desc: 'unknown', int_value: -1}
]

export const KYBStatuses = [
  {desc: 'Pending', str_value: 'PENDING'},
  {desc: 'Submitted', str_value: 'SUBMITTED'},
  {desc: 'Approved', str_value: 'APPROVED'},
  {desc: 'KYC Blocked', str_value: 'KYB_APPROVED_KYC_BLOCK'},
  {desc: 'Denied', str_value: 'DENIED'}
]

export const LegalStatuses = [
  {desc: 'Pending', str_value: 'PENDING'},
  {desc: 'Approved', str_value: 'APPROVED'},
  {desc: 'Legal Override Approved', str_value: 'LEGAL_OVERRIDE_APPROVED'},
  {desc: 'Denied', str_value: 'DENIED'},
  {desc: 'Expired', str_value: 'EXPIRED'}
]

/// ROLES
// Each role grants access only to its own surface area. ADMIN gates the
// Configuration pages (Admin Panel, Cron) and nothing else; it does NOT
// inherit access to HR/TECH/COMPLIANCE/EXEC/EMPLOYEE pages. An admin who
// needs broader access must be assigned the additional roles in the admin UI.
//
// The canSeeX helpers below accept either an array of role strings or
// (for legacy callers) a single string.

import { store } from '../app/store';
import { api }  from '../app/api';

export const getRoles = async (thisUUID) => {
  try {
    const result = await store.dispatch(
      api.endpoints.adminRole.initiate({uuid: thisUUID})
    );
    const payload = result?.data?.admin_role;
    if (!payload) return { roles: [], status: 'PENDING' };
    return {
      roles: payload.roles || [],
      status: payload.status || 'ACTIVE',
    };
  } catch (error) {
    console.error("Error fetching admin roles:", error);
    return { roles: [], status: 'PENDING' };
  }
}

// Normalize input so each canSeeX helper accepts either an array of roles
// or a single role string. Returning a Set keeps the membership checks cheap
// even on the rare admin with many roles.
const asRoleSet = (r) => {
  if (Array.isArray(r)) return new Set(r);
  if (typeof r === 'string') return new Set([r]);
  return new Set();
};

const hasAnyRole = (r, allowed) => {
  const roles = asRoleSet(r);
  return allowed.some(role => roles.has(role));
};

// Configuration (Admin Panel, Cron) — ADMIN role only.
export const canSeeAdmin = (r) => hasAnyRole(r, ['ADMIN']);

// SUPER_ADMIN — the only role that can see, grant or revoke the protected roles
// (SUPER_ADMIN itself and CONFIDENTIAL_REVIEWER) on the Admin Panel. Opens no
// page of its own. Mirrors PROTECTED_ROLES in api/app/db/users.py, which is
// what actually enforces it: for anyone else the API leaves those roles out of
// every list, so hiding the checkboxes here is presentation, not the guard.
export const canManageProtectedRoles = (r) => hasAnyRole(r, ['SUPER_ADMIN']);

// TECH pages
export const canSeeTech = (r) => hasAnyRole(r, ['TECH']);

// HR pages
export const canSeeHR = (r) => hasAnyRole(r, ['HR']);

// COMPLIANCE pages
export const canSeeCompliance = (r) => hasAnyRole(r, ['COMPLIANCE']);

// EXEC pages
export const canSeeExec = (r) => hasAnyRole(r, ['EXEC']);

// FINANCE pages
export const canSeeFinance = (r) => hasAnyRole(r, ['FINANCE']);

// FINTECH — fintech due-diligence work on the Reg S-P software register.
export const canSeeFintech = (r) => hasAnyRole(r, ['FINTECH']);

// WORK side (the wealth-management workspace). Users without this role
// don't see the Hub/Work toggle in the sidebar and can't hit any /work/*
// URL — the routePermissions guard in Root.jsx bounces them to /dashboard.
export const canSeeWork = (r) => hasAnyRole(r, ['WORK']);

// WORK_ADMIN — the elevated tier of the Work side: things a client-facing
// colleague (a CSA) needs but which aren't day-to-day advisor work. It opens
// no page of its own, so it is NOT in the sidebar's backstage set; it only
// unlocks actions inside Work pages the person can already reach.
export const canSeeWorkAdmin = (r) => hasAnyRole(r, ['WORK_ADMIN']);

// The areas of the Work side, one role each — the same shape as the department
// roles behind the Admin button. WORK gets you onto the Work side; these decide
// which of its pages you see. Granted on Super Admin -> Work Access. Mirrors
// WORK_AREA_ROLES in api/app/db/users.py, and the server enforces each one
// (require_work_area_scoped) — hiding a page here is presentation, not the guard.
export const WORK_AREA_ROLES = [
  { role: 'WORK_PORTAL_CLIENTS', key: 'isWorkPortalClients', label: 'Portal Clients' },
  { role: 'WORK_DOCUMENTS',      key: 'isWorkDocuments',     label: 'Documents' },
  { role: 'WORK_CRM',            key: 'isWorkCrm',           label: 'CRM' },
  { role: 'WORK_TASKS',          key: 'isWorkTasks',         label: 'Tasks' },
];

// "Log in as" a CLIENT PORTAL user. ADMIN or WORK_ADMIN — mirrors the server's
// IMPERSONATE_CLIENT_ROLES in routers/clients.py; the two must agree or the
// button appears and then 403s. Deliberately its own helper rather than a
// bare isAdmin check at the call site: the next elevated Work action should
// reuse this shape, and the EMPLOYEE and B2B impersonation flows are separate
// gates that this must not be used for.
export const canImpersonateClients = (r) => hasAnyRole(r, ['ADMIN', 'WORK_ADMIN']);

// CONFIDENTIAL_REVIEWER — the only role that can read Role Alignment answers
// (the questionnaire and the manager's assessment) and use Ask over them.
// Mirrors require_confidential_reviewer in api/app/dependencies.py. TECH runs
// the process without the answers; this role reads them and changes nothing —
// the manager checkboxes stay TECH-only.
export const canReadConfidentialForms = (r) => hasAnyRole(r, ['CONFIDENTIAL_REVIEWER']);

// ENTREPRENEUR section — the employee-side surface for setting up B2B firms
// and their users. Gated like the other role pages.
export const canSeeEntrepreneur = (r) => hasAnyRole(r, ['ENTREPRENEUR']);

// MARKETING section — authoring the collateral library the B2B firms read, and
// the activity report over it. Split out of ENTREPRENEUR: setting a firm up and
// deciding what that firm is given to read are different jobs, and roles here
// don't inherit, so anyone doing both holds both.
export const canSeeMarketing = (r) => hasAnyRole(r, ['MARKETING']);

// BETA — the standing early-access group for features being piloted before
// general release (first use: the Your Day AI briefings). Not a department:
// membership is expected to change often, which is the point. Pages gated on it
// should ALSO respect their own feature flag, so a pilot can be killed for
// everyone without unassigning the role one person at a time.
export const canSeeBeta = (r) => hasAnyRole(r, ['BETA']);

// Employee-page access is implicit for every ACTIVE admin and is not tied
// to any specific role assignment.
export const canSeeEmployee = (_roles, status) => status === 'ACTIVE';

export const getRoleJSON = async (UUID) => {
  const { roles, status } = await getRoles(UUID);
  return {
    roles,
    status,
    isAdmin:        canSeeAdmin(roles),
    isSuperAdmin:   canManageProtectedRoles(roles),
    isTech:         canSeeTech(roles),
    isHR:           canSeeHR(roles),
    isCompliance:   canSeeCompliance(roles),
    isExec:         canSeeExec(roles),
    isFinance:      canSeeFinance(roles),
    isFintech:      canSeeFintech(roles),
    isWork:         canSeeWork(roles),
    isWorkAdmin:    canSeeWorkAdmin(roles),
    ...Object.fromEntries(WORK_AREA_ROLES.map(a => [a.key, hasAnyRole(roles, [a.role])])),
    canImpersonateClients: canImpersonateClients(roles),
    isEntrepreneur: canSeeEntrepreneur(roles),
    isMarketing:    canSeeMarketing(roles),
    isConfidentialReviewer: canReadConfidentialForms(roles),
    isBeta:         canSeeBeta(roles),
    isEmployee:     canSeeEmployee(roles, status),
  };
}

/**
 * Convert JSON data to a File object (image)
 * @param {Object|Array} jsonData - The JSON data to convert
 * @param {string} filename - The filename for the generated file (default: 'json-data.png')
 * @param {Object} options - Styling options for the image
 * @returns {Promise<File>} Promise that resolves to a File object
 */
export const jsonToImageFile = (jsonData, filename = 'json-data.png', options = {}) => {
  const {
    width = 800,
    backgroundColor = '#ffffff',
    textColor = '#000000',
    fontSize = 22,
    fontFamily = 'Arial, sans-serif',
    padding = 20,
    lineHeight = 2,
    indentSize = 20
  } = options;

  // Helper function to check if a value should be included
  const shouldIncludeValue = (value) => {
    // Exclude null, undefined
    if (value === null || value === undefined) return false;
    
    // Include numbers
    if (typeof value === 'number') return true;
    
    // For strings, exclude empty strings
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    
    // For arrays, only include if not empty and process items
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    
    // For objects, only include if not empty and has valid properties
    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }
    
    // Exclude all other types (booleans, etc.)
    return false;
  };

  // Convert JSON to human-readable format
  const formatJsonToReadable = (obj, indent = 0) => {
    const lines = [];
    const indentStr = ' '.repeat(indent);

    if (Array.isArray(obj)) {
      // Filter array to only include valid items
      const validItems = obj.filter(item => shouldIncludeValue(item));
      
      validItems.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          const nestedLines = formatJsonToReadable(item, indent + indentSize);
          if (nestedLines.length > 0) {
            lines.push(`${indentStr}Item ${index + 1}:`);
            lines.push(...nestedLines);
          }
        } else if (typeof item === 'string' || typeof item === 'number') {
          lines.push(`${indentStr}Item ${index + 1}: ${item}`);
        }
      });
    } else if (typeof obj === 'object' && obj !== null) {
      Object.entries(obj).forEach(([key, value]) => {
        if (!shouldIncludeValue(value)) return; // Skip invalid values
        
        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        
        if (typeof value === 'object' && value !== null) {
          const nestedLines = formatJsonToReadable(value, indent + indentSize);
          if (nestedLines.length > 0) {
            lines.push(`${indentStr}${formattedKey}:`);
            lines.push(...nestedLines);
          }
        } else if (typeof value === 'string' || typeof value === 'number') {
          lines.push(`${indentStr}${formattedKey}: ${value}`);
        }
      });
    }

    return lines;
  };

  return new Promise((resolve, reject) => {
    try {
      // Create canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Convert JSON to readable format
      const lines = formatJsonToReadable(jsonData);

      // Set fonts for measurement
      const normalFont = `${fontSize}px ${fontFamily}`;
      const boldFont = `bold ${fontSize}px ${fontFamily}`;

      // Calculate dimensions
      ctx.font = boldFont; // Use bold font for measurement (widest case)
      const maxLineWidth = Math.max(...lines.map(line => {
        // Measure the full line with bold values
        const colonIndex = line.indexOf(': ');
        if (colonIndex > -1) {
          const keyPart = line.substring(0, colonIndex + 2);
          const valuePart = line.substring(colonIndex + 2);
          ctx.font = normalFont;
          const keyWidth = ctx.measureText(keyPart).width;
          ctx.font = boldFont;
          const valueWidth = ctx.measureText(valuePart).width;
          return keyWidth + valueWidth;
        } else {
          ctx.font = normalFont;
          return ctx.measureText(line).width;
        }
      }));

      const actualWidth = Math.max(width, maxLineWidth + (padding * 2));
      const actualLineHeight = fontSize * lineHeight;
      const totalHeight = (lines.length * actualLineHeight) + (padding * 2);

      // Set canvas size
      canvas.width = actualWidth;
      canvas.height = totalHeight;

      // Fill background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, actualWidth, totalHeight);

      // Set text properties
      ctx.fillStyle = textColor;
      ctx.textBaseline = 'top';

      // Draw each line with proper formatting
      lines.forEach((line, index) => {
        const y = padding + (index * actualLineHeight);
        const colonIndex = line.indexOf(': ');
        
        if (colonIndex > -1) {
          // Line has a key-value pair
          const keyPart = line.substring(0, colonIndex + 2); // Include ": "
          const valuePart = line.substring(colonIndex + 2);
          
          // Draw key part in normal font
          ctx.font = normalFont;
          ctx.fillText(keyPart, padding, y);
          
          // Calculate where value starts
          const keyWidth = ctx.measureText(keyPart).width;
          
          // Draw value part in bold font
          ctx.font = boldFont;
          ctx.fillText(valuePart, padding + keyWidth, y);
        } else {
          // Line is a header/section (draw in normal font)
          ctx.font = normalFont;
          ctx.fillText(line, padding, y);
        }
      });

      // Convert canvas to blob, then to File
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], filename, { type: 'image/png' });
          resolve(file);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/png');

    } catch (error) {
      reject(error);
    }
  });
};